<script setup lang="ts">
import { computed } from 'vue'

import ActionOutcomeSummary, {
  type OutcomeBucket,
  type OutcomeKind,
} from './ActionOutcomeSummary.vue'

import { useInsightActionCommands } from '../composables/useInsightActionCommands'
import { useReasonText } from '../composables/useReasonText'
import type { InsightActionItemStatus, RunItemView } from '../models/insightAction.types'
import { isRetryableReason } from '../models/insightActionReason'
import { useInsightActionStore } from '../stores/insightActionStore'

import { PLATFORM_ACTIONS_ROUTE } from '@/features/platform-actions'
import { useI18n } from '@/shared/i18n'

const { t } = useI18n()
const store = useInsightActionStore()
const commands = useInsightActionCommands()
const { reason } = useReasonText()

const items = computed(() => store.run?.items ?? [])
const submitted = computed(() => store.run?.totalItems ?? items.value.length)

const BUCKET_OF: Partial<Record<InsightActionItemStatus, OutcomeKind>> = {
  succeeded: 'applied',
  reverted: 'applied',
  awaiting_approval: 'waiting',
  skipped: 'skipped',
  failed: 'failed',
}

function sharedMessage(rows: RunItemView[]): string {
  const messages = new Set(
    rows.map((row) => row.errorMessage || reason(row.reasonCode).description).filter(Boolean),
  )
  return messages.size === 1 ? ([...messages][0] ?? '') : ''
}

const buckets = computed<OutcomeBucket[]>(() => {
  const grouped = new Map<OutcomeKind, RunItemView[]>()
  for (const item of items.value) {
    const kind = BUCKET_OF[item.status]
    if (!kind) continue
    grouped.set(kind, [...(grouped.get(kind) ?? []), item])
  }

  return [...grouped.entries()].map(([kind, rows]) => ({
    kind,
    count: rows.length,
    ...(kind === 'waiting'
      ? { to: PLATFORM_ACTIONS_ROUTE, toLabel: t('insightActions.outcome.goToApprovals') }
      : {}),
    rows: rows.map((row) => ({
      rowKey: row.rowKey,
      label: row.summary || row.rowKey,
      retryable: kind === 'failed' && isRetryableReason(row.reasonCode),
    })),
    message: sharedMessage(rows),
  }))
})
</script>

<template>
  <ActionOutcomeSummary
    :submitted="submitted"
    :buckets="buckets"
    @retry="commands.retryRows($event)"
  />
</template>
